<template>
    <div>
        <h1 class="tw-text-3xl">
            Neue Auslagenerstattung
        </h1>

        <hr class="tw-my-4">

        <div class="tw-bg-danger-secondary tw-rounded-lg tw-p-4 tw-text-danger-primary tw-mb-4"
             v-if="!! errors && errors.length > 0">
            <strong>
                Validierungsfehler
            </strong>

            <ul class="tw-mt-2">
                <li v-for="error in errors">
                    {{ error }}
                </li>
            </ul>
        </div>

        <div v-show="step === -1" class="md:tw-w-1/2">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                Was möchtest du erstellen?
            </div>

            <div class="tw-flex tw-items-center tw-pr-6 tw-py-1">
                <div class="tw-flex tw-items-center tw-pr-6 tw-py-1" v-for="expenseType in ExpenseTypes">
                    <radio
                            :id="'expense_type_' + expenseType.value"
                            :initial-value="expenseType.value"
                            name="expense_type"
                            v-model="travel_expense_report.expense_type"
                    ></radio>
                    <label class="tw-ml-1" :for="'expense_type_' + expenseType.value" v-text="expenseType.title"></label>
                </div>
            </div>
        </div>

        <div v-show="step === 0">
            <table id="expense_table" class="cu-table cu-table-no-padding-x">
                <tr>
                    <th>Datum</th>
                    <th>Kostenart</th>
                    <th>Kosten</th>
                    <th>Kostenstelle</th>
                    <th>Kostenträger</th>
                    <th></th>
                </tr>

                <tr class="expense_row" v-for="(cost, i) in expense_reimbursement.costs">
                    <td>
                        <div class="cu-input-simple">
                            <input type="date"
                                    class="expense_date"
                                    name="expense_date[]"
                                    v-model="cost.date"
                            >
                        </div>
                    </td>
                    <td>
                        <div class="cu-input-simple">
                            <input type="text"
                                    class="expense_type"
                                    name="expense_type[]"
                                    v-model="cost.type"
                            >
                        </div>
                    </td>
                    <td>
                        <div class="cu-input-simple">
                            <input type="number"
                                    class="expense_costs"
                                    name="expense_costs[]"
                                    step=".01"
                                    v-model="cost.amount"
                            >
                        </div>
                    </td>
                    <td>
                        <div class="cu-input-simple">
                            <select name="expense_cost_center[]" id="expense_cost_center" v-model="cost.cost_carrier_center">
                                <option value="">
                                    Kostenstelle auswählen...
                                </option>

                                <option v-for="costCenter in costCenters" :value="costCenter.id">
                                    {{ costCenter.name }}
                                </option>
                            </select>
                        </div>
                    </td>
                    <td>
                        <div class="cu-input-simple">
                            <select name="expense_cost_carrier[]" id="expense_cost_carrier" v-model="cost.cost_carrier">
                                <option value="">
                                    Kostenträger auswählen...
                                </option>

                                <option v-for="costCarrier in costCarriers" :value="costCarrier.id">
                                    {{ costCarrier.label }}
                                </option>
                            </select>
                        </div>
                    </td>
                    <td>
                        <button v-show="expense_reimbursement.costs.length > 1" @click="deleteExpense(cost)" type="button" class="cu-btn-default" title="Löschen">
                            <i class="fa fa-trash-o"></i>
                        </button>
                    </td>
                </tr>

                <tr>
                    <td>
                        <button type="button" class="cu-btn-default" id="other_cost_button" @click="addExpense">
                            <i class="fa fa-plus"></i> weitere Zeile hinzufügen
                        </button>
                    </td>

                    <td class="tw-text-right">
                        <span>
                            Gesamtkosten:
                        </span>
                        <p class="tw-inline tw-ml-5 other_cost_total total_cost tw-font-bold"
                            v-text="Helpers.reimbursementCostsTotal() + ' €'"
                        ></p>
                    </td>
                </tr>
            </table>

            <div>
                <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                    Beleg (Pflicht)
                </div>

                <div class="tw-mt-2 tw-flex">
                    <multi-file-upload
                        id="expense_reimbursement_attachments"
                        key="expense_reimbursement_attachments"
                        index="expense_reimbursement_attachments"
                        block-layout
                        accept-file-types="image/*,application/pdf"
                        :old="oldData"
                        :attachment-type-id="expenseReimbursementAttachmentTypeId"
                        @update-attachment-ids="updateAttachmentIds($event, expenseReimbursementAttachmentTypeId)"
                    ></multi-file-upload>
                </div>

                <div class="tw-italic tw-mt-2">
                    Hinweis: Es sind nur Bilder & PDFs erlaubt
                </div>
            </div>

            <div class="tw-flex tw-items-center tw-py-1 tw-mt-4">
                <check-box
                        id="expense_confirmation"
                        :initial-value="true"
                        name="expense_confirmation"
                        v-model="expense_reimbursement.expense_confirmation"
                ></check-box>
                <label class="tw-ml-1" :for="'expense_confirmation'">
                    Ich bestätige die Auslagenerstattung und verzichte auf eine Unterschrift.
                </label>
            </div>

            <div class="tw-mt-4">
                <button v-if="!isEditWizard" type="button" class="tw-w-24 cu-btn-default tw-mr-1" @click="previousStep">
                    Zurück
                </button>

                <button class="tw-w-24 cu-btn tw-mr-1" @click="save(true)">
                    Speichern
                </button>

                <button class="tw-w-24 cu-btn-default" @click="save(false)">
                    Entwurf
                </button>
            </div>
        </div>

        <div v-show="step === 1" class="md:tw-w-1/2">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                1/9 Reiseroute
            </div>

            <bordered-input type="textarea">
                <template #label>
                    <label for="route">
                        Wohin bist du gereist?
                    </label>
                </template>
                <template #input>
                    <textarea name="route"
                              id="route"
                              placeholder="Ziel + Zwischenstopps

Bsp.: Du fährst von der Firma nach Berlin und zurück.
Route: Kabelsketal-Berlin-Kabelsketal. Keine Rasthöfe aufzählen! "
                              rows="6"
                              v-model="travel_expense_report.route"
                    ></textarea>
                </template>
            </bordered-input>
        </div>

        <div v-show="step === 2" class="md:tw-w-1/2">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                2/9 Anlass der Reise
            </div>

            <bordered-input type="textarea">
                <template #label>
                    <label for="reason_for_trip">
                        Aus welchem Grund bist du verreist?
                    </label>
                </template>
                <template #input>
                    <textarea name="reason_for_trip"
                              id="reason_for_trip"
                              placeholder="Grund der Geschäftsreise: Meeting mit Kunden, Akquise, ..."
                              rows="6"
                              v-model="travel_expense_report.reason_for_trip"
                    ></textarea>
                </template>
            </bordered-input>
        </div>

        <div v-show="step === 3" class="md:tw-w-4/5">
            <div class="tw-text-lg tw-mb-4 tw-font-bold">
                3/9 Dauer der Reise
            </div>

            <div class="tw-flex tw-items-center">
                Wie lange bist du verreist?
            </div>

            <div class="tw-mt-4">
                <table id="period" class="cu-table">
                    <tr>
                        <th></th>
                        <th>Datum</th>
                        <th>von</th>
                        <th>bis</th>

                        <th>Stunden</th>
                        <th></th>
                    </tr>

                    <tr class="period_row" v-for="(day, i) in travel_expense_report.travel_information.period">
                        <td>
                            <p class="day">Tag {{ i + 1 }}</p>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="date"
                                       class="period_day"
                                       :id="'period_day' + i"
                                       v-model="day.date"
                                       placeholder="Datum"
                                       :readonly="day.date !== travel_expense_report.travel_information.period[0].date"
                                       @change="refreshDays"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="time"
                                       class="period_from"
                                       :id="'period_from' + i"
                                       v-model="day.from"
                                       placeholder="von"
                                       :disabled="day.entire_day || travel_expense_report.travel_information.period.length >= 2 && i !== 0 && travel_expense_report.travel_information.had_overnight"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="time"
                                       class="period_to"
                                       :id="'period_to' + i"
                                       v-model="day.to"
                                       placeholder="bis"
                                       :disabled="day.entire_day || travel_expense_report.travel_information.period.length > 1 && i === 0 && travel_expense_report.travel_information.had_overnight"
                                       @change="minTime(day)"
                                >
                            </div>
                        </td>

                        <td>
                            <p class="period_hours tw-font-bold" v-text="Helpers.getHoursForDayFormatted(day)"></p>
                            <input type="hidden" class="period_hours_data" id="period_hours_data1" name="period_hours[]"
                                   value="">
                        </td>
                        <td>
                            <button v-show="i > 0" @click="deleteDay(day)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>
                </table>

                <button type="button" class="cu-btn-default tw-mt-2" id="period_button" @click="addDay" :disabled="!travel_expense_report.travel_information.period[0].date">
                    <i class="fa fa-plus"></i> weiteren Tag hinzufügen
                </button>

                <div v-if="travel_expense_report.travel_information.period && travel_expense_report.travel_information.period.length > 1">
                    <div class="tw-flex tw-items-center tw-gap-4 tw-my-2">
                        <span>Gab es auswärtige Übernachtungen?</span>

                        <label class="tw-flex tw-items-center tw-gap-1">
                            <input
                                type="radio"
                                :value="true"
                                v-model="travel_expense_report.travel_information.had_overnight"
                                @change="refreshDays">
                            <span>Ja</span>
                        </label>

                        <label class="tw-flex tw-items-center tw-gap-1">
                            <input
                                type="radio"
                                :value="false"
                                v-model="travel_expense_report.travel_information.had_overnight"
                                checked
                                @change="refreshDays">
                            <span>Nein</span>
                        </label>
                    </div>

                    <span>
                        Hinweis: Eine Übernachtung im steuerlichen Sinne liegt vor, wenn tatsächlich außerhalb der eigenen Wohnung übernachtet wurde.
                    </span>
                </div>
            </div>
        </div>

        <div v-show="step === 4" class="md:tw-w-4/5">
            <div class="tw-text-lg tw-font-bold">
                4/9 Wie bist du verreist?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-column">
                <div class="tw-flex tw-items-center tw-pr-6 tw-py-1" v-for="travelType in TravelTypes">
                    <radio
                            :id="'travel_type_' + travelType.value"
                            :initial-value="travelType.value"
                            name="travel_type"
                            v-model="travel_expense_report.travel_information.transportation_type"
                    ></radio>
                    <label class="tw-ml-1" :for="'travel_type_' + travelType.value" v-text="travelType.title"></label>
                </div>
            </div>

            <div class="tw-mt-8"
                 v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_PUBLIC_TRANSPORTATION">
                <div class="tw-font-bold tw-mb-4">
                    andere Verkehrsmittel
                </div>

                <div class="tw-font-bold">
                    Wurden die Fahrtkosten selbst gezahlt?
                </div>

                <div class="tw-mt-1 tw-flex tw-flex-wrap tw-w-1/3">
                    <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                        <radio
                            id="public_transportation_self_paid_true"
                            name="public_transportation_self_paid"
                            :initial-value="true"
                            v-model="travel_expense_report.travel_information.public_transportation.self_paid"
                        ></radio>
                        <label class="tw-ml-1" :for="'public_transportation_self_paid_true'">
                            Ja
                        </label>
                    </div>

                    <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                        <radio
                            id="public_transportation_self_paid_false"
                            name="public_transportation_self_paid"
                            :initial-value="false"
                            v-model="travel_expense_report.travel_information.public_transportation.self_paid"
                        ></radio>
                        <label class="tw-ml-1" :for="'public_transportation_self_paid_false'">
                            Nein
                        </label>
                    </div>
                </div>

                <table id="public_transportation" class="cu-table cu-table-no-padding-x tw-mt-4" v-show="travel_expense_report.travel_information.public_transportation.self_paid">
                    <tr>
                        <th>Art</th>
                        <th>Kosten in €</th>
                        <th></th>
                    </tr>

                    <tr class="public_transportation_row"
                        v-for="cost in travel_expense_report.travel_information.public_transportation.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="text"
                                       class="public_transportation_type"
                                       v-model="cost.type"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="public_transportation_costs"
                                       name="public_transportation_costs[]"
                                       step=".01"
                                       v-model="cost.amount"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.public_transportation.costs.length > 1" @click="deletePublicTransportationCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="public_transportation_button"
                                    @click="addPublicTransportationCost">
                                <span class="fa fa-plus"></span> weitere Zeile hinzufügen
                            </button>
                        </td>
                        <td class="tw-text-right">
                            Gesamtkosten öffentliche Verkehrsmittel:
                            <p class="tw-inline tw-ml-5 tw-font-bold public_transportation_total_cost total_cost"
                               id="public_transportation_total_cost"
                               v-text="Helpers.publicTransportationTotalCosts() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>
            </div>

            <div v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_PUBLIC_TRANSPORTATION && travel_expense_report.travel_information.public_transportation.self_paid">
                <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                    Beleg (Pflicht)
                </div>

                <div class="tw-mt-2 tw-flex">
                    <multi-file-upload
                        id="public_transportation_attachments"
                        key="public_transportation_attachments"
                        index="public_transportation_attachments"
                        block-layout
                        accept-file-types="image/*,application/pdf"
                        :old="oldData"
                        :attachment-type-id="publicTransportationAttachmentTypeId"
                        @update-attachment-ids="updateAttachmentIds($event, publicTransportationAttachmentTypeId)"
                    ></multi-file-upload>
                </div>

                <div class="tw-italic tw-mt-2">
                    Hinweis: Es sind nur Bilder & PDFs erlaubt
                </div>
            </div>

            <div v-show="travel_expense_report.travel_information.transportation_type === Constants.TRANSPORTATION_TYPE_COMPANY_CAR && travel_expense_report.travel_information.company_car.self_paid">
                <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                    Beleg (Pflicht)
                </div>

                <div class="tw-mt-2 tw-flex">
                    <multi-file-upload
                        id="company_car_attachments"
                        key="company_car_attachments"
                        index="company_car_attachments"
                        block-layout
                        accept-file-types="image/*,application/pdf"
                        :old="oldData"
                        :attachment-type-id="companyCarAttachmentTypeId"
                        @update-attachment-ids="updateAttachmentIds($event, companyCarAttachmentTypeId)"
                    ></multi-file-upload>
                </div>

                <div class="tw-italic tw-mt-2">
                    Hinweis: Es sind nur Bilder & PDFs erlaubt
                </div>
            </div>
        </div>

        <div v-show="step === 5" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                5/9 Übernachtung
            </div>

            <div class="tw-font-bold tw-mt-2">
                Wurden die Übernachtungskosten selbst gezahlt?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="overnight_self_paid_true"
                        name="overnight_self_paid"
                        :initial-value="true"
                        v-model="travel_expense_report.travel_information.overnight_stay.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'overnight_self_paid_true'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="overnight_self_paid_false"
                        name="overnight_self_paid"
                        :initial-value="false"
                        v-model="travel_expense_report.travel_information.overnight_stay.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'overnight_self_paid_false'">
                        Nein
                    </label>
                </div>
            </div>

            <div v-if="travel_expense_report.travel_information.overnight_stay.self_paid === true">
                <div class="tw-py-4">
                    Es sind von der Hotelrechnung <b>ausschließlich</b> die Übernachtungskosten einzutragen,
                    <b>keine</b> Kosten für Frühstück, Abendessen, Getränke, Minibar, Parkplatz etc.
                </div>

                <div class="tw-mt-4">
                    <div class="tw-flex">
                        <div class="tw-w-1/2">
                            <bordered-input>
                                <template #label>
                                    <label for="overnight_stay_place">
                                        Name und Ort des Hotels
                                    </label>
                                </template>
                                <template #input>
                                    <input type="text"
                                           name="overnight_stay_place"
                                           id="overnight_stay_place"
                                           placeholder="Name und Ort des Hotels"
                                           v-model="travel_expense_report.travel_information.overnight_stay.hotel"
                                    >
                                </template>
                            </bordered-input>
                        </div>

                        <div class="tw-w-1/2 tw-pl-4" v-if="travel_expense_report.travel_information.overnight_stay.self_paid !== true">
                            <bordered-input>
                                <template #label>
                                    <label for="overnight_stay_place">
                                        Zahlungsart
                                    </label>
                                </template>
                                <template #input>
                                    <select id="overnight_stay_payment_type"
                                            name="overnight_stay_payment_type"
                                            v-model="travel_expense_report.travel_information.overnight_stay.payment_method"
                                    >
                                        <option v-for="overnightPaymentType in OvernightPaymentTypes"
                                                :value="overnightPaymentType.value"
                                                v-text="overnightPaymentType.title"
                                        ></option>
                                    </select>
                                </template>
                            </bordered-input>
                        </div>
                    </div>

                    <table id="overnight_stay" class="cu-table cu-table-no-padding-x">
                        <tr>
                            <th>Name</th>
                            <th>Anzahl der Nächte</th>
                            <th>Kosten/Nacht</th>
                            <th>Kosten</th>
                            <th></th>
                        </tr>

                        <tr class="overnight_stay_row"
                            v-for="cost in travel_expense_report.travel_information.overnight_stay.costs">
                            <td>
                                <div class="cu-input-simple">
                                    <input type="text"
                                           class="overnight_stay_persons"
                                           placeholder=""
                                           v-model="cost.person_name"
                                    >
                                </div>
                            </td>
                            <td>
                                <div class="cu-input-simple">
                                    <input type="number" min="0"
                                           class="overnight_stay_night overnight_stays"
                                           placeholder=""
                                           v-model="cost.number_of_nights"
                                    >
                                </div>
                            </td>
                            <td>
                                <div class="cu-input-simple">
                                    <input type="number" min="0"
                                           class="overnight_stay_cost_night overnight_stays"
                                           placeholder=""
                                           v-model="cost.amount_per_night"
                                    >
                                </div>
                            </td>
                            <td>
                                <p class="overnight_stay_costs"
                                   id="overnight_stay_costs0"
                                   v-text="Helpers.overnightStayCosts(cost) + ' €'"
                                ></p>
                            </td>
                            <td>
                                <button v-show="travel_expense_report.travel_information.overnight_stay.costs.length > 1" @click="deleteOvernightStayCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                    <i class="fa fa-trash-o"></i>
                                </button>
                            </td>
                        </tr>

                        <tr>
                            <td colspan="2">
                                <button type="button" class="cu-btn-default" id="overnight_stay_button"
                                        @click="addOvernightStayCosts">
                                    <i class="fa fa-plus"></i> weitere Person hinzufügen
                                </button>
                            </td>
                            <td class="tw-text-right">
                                Gesamtkosten Übernachtung:
                            </td>
                            <td>
                                <p class="total_cost tw-font-bold"
                                   v-text="Helpers.overnightStayTotalCosts() + ' €'"
                                ></p>
                            </td>
                        </tr>
                    </table>

                    <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                        Beleg (Pflicht)
                    </div>

                    <div class="tw-mt-2 tw-flex">
                        <multi-file-upload
                            id="overnight_attachments"
                            key="overnight_attachments"
                            index="overnight_attachments"
                            block-layout
                            accept-file-types="image/*,application/pdf"
                            :old="oldData"
                            :attachment-type-id="overnightAttachmentTypeId"
                            @update-attachment-ids="updateAttachmentIds($event, overnightAttachmentTypeId)"
                        ></multi-file-upload>
                    </div>

                    <div class="tw-italic tw-mt-2">
                        Hinweis: Es sind nur Bilder & PDFs erlaubt
                    </div>
                </div>
            </div>
        </div>

        <div v-show="step === 6" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                6/9 sonstige Kosten
            </div>

            <div class="tw-font-bold tw-mt-2">
                Wurden sonstige Kosten selbst gezahlt?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="other_cost_self_paid_true"
                        name="other_cost_self_paid"
                        :initial-value="true"
                        v-model="travel_expense_report.travel_information.other_cost.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'other_cost_self_paid_true'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                        id="other_cost_self_paid_false"
                        name="other_cost_self_paid"
                        :initial-value="false"
                        v-model="travel_expense_report.travel_information.other_cost.self_paid"
                    ></radio>
                    <label class="tw-ml-1" :for="'other_cost_self_paid_false'">
                        Nein
                    </label>
                </div>
            </div>

            <div v-if="travel_expense_report.travel_information.other_cost.self_paid === true">
                <table id="other_cost" class="cu-table cu-table-no-padding-x">
                    <tr>
                        <th>Art</th>
                        <th>Kosten</th>
                        <th></th>
                    </tr>

                    <tr class="other_cost_row" v-for="cost in travel_expense_report.travel_information.other_cost.costs">
                        <td>
                            <div class="cu-input-simple">
                                <input type="text"
                                       class="other_cost_type"
                                       name="other_cost_type[]"
                                       v-model="cost.type"
                                >
                            </div>
                        </td>
                        <td>
                            <div class="cu-input-simple">
                                <input type="number"
                                       class="other_costs"
                                       name="other_costs[]"
                                       step=".01"
                                       v-model="cost.amount"
                                >
                            </div>
                        </td>
                        <td>
                            <button v-show="travel_expense_report.travel_information.other_cost.costs.length > 1" @click="deleteOtherCost(cost)" type="button" class="cu-btn-default" title="Löschen">
                                <i class="fa fa-trash-o"></i>
                            </button>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            <button type="button" class="cu-btn-default" id="other_cost_button" @click="addOtherCosts">
                                <i class="fa fa-plus"></i> weitere Zeile hinzufügen
                            </button>
                        </td>

                        <td class="tw-text-right">
                            <span>
                                Gesamtkosten sonstige:
                            </span>
                            <p class="tw-inline tw-ml-5 other_cost_total total_cost tw-font-bold"
                               v-text="Helpers.otherCostsTotal() + ' €'"
                            ></p>
                        </td>
                    </tr>
                </table>

                <div class="tw-mb-2 tw-mt-6 tw-font-bold">
                    Beleg (Pflicht)
                </div>

                <div class="tw-mt-2 tw-flex">
                    <multi-file-upload
                        id="other_cost_attachments"
                        key="other_cost_attachments"
                        index="other_cost_attachments"
                        block-layout
                        accept-file-types="image/*"
                        :old="oldData"
                        :attachment-type-id="otherCostAttachmentTypeId"
                        @update-attachment-ids="updateAttachmentIds($event, otherCostAttachmentTypeId)"
                    ></multi-file-upload>
                </div>

                <div class="tw-italic tw-mt-2">
                    Hinweis: Es sind nur Bilder & PDFs erlaubt
                </div>
            </div>
        </div>

        <div v-show="step === 7" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                7/9 Verpflegungsmehraufwand
            </div>

            <div class="tw-font-bold tw-mt-2">
                Soll ein möglicher Verpflegungsmehraufwand ausbezahlt werden?
            </div>

            <div class="tw-mt-2 tw-flex tw-flex-wrap tw-w-1/3">
                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                            id="daily_allowance_1"
                            name="daily_allowance"
                            :initial-value="1"
                            v-model="travel_expense_report.travel_information.daily_allowance"
                    ></radio>
                    <label class="tw-ml-1" :for="'daily_allowance_1'">
                        Ja
                    </label>
                </div>

                <div class="tw-flex tw-items-center tw-w-1/2 tw-pr-4 tw-py-1">
                    <radio
                            id="daily_allowance_0"
                            name="daily_allowance"
                            :initial-value="0"
                            v-model="travel_expense_report.travel_information.daily_allowance"
                    ></radio>
                    <label class="tw-ml-1" :for="'daily_allowance_0'">
                        Nein
                    </label>
                </div>
            </div>

            <div class="tw-text-blue-grey tw-italic"
                 v-show="! travel_expense_report.travel_information.daily_allowance">
                    Erstatteter Verpflegungsmehraufwand ist steuerlich nicht nochmals ansetzbar. Bei Auslandsreisen werden nur die deutschen
                    Pauschalen erstattet; in der Einkommensteuer ist lediglich die Differenz zu den Auslandspauschalen absetzbar.
            </div>

            <div class="tw-mt-4" v-show="travel_expense_report.travel_information.daily_allowance">
                <table class="cu-table cu-table-no-padding-x">
                    <thead>
                    <tr>
                        <th>Reisedauer</th>
                        <th>pauschaler Tagessatz</th>
                        <th>Anzahl laut o.a. Reisedauer</th>
                        <th>Betrag</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>Ganzer Tag</td>
                        <td v-text="Helpers.formatPrice(rate_settings.ABOVE_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWith24Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWith24Hours() * rate_settings.ABOVE_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>Tage über 8 Std.</td>
                        <td v-text="Helpers.formatPrice(rate_settings.FROM_8_TO_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWith8To24Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWith8To24Hours() * rate_settings.FROM_8_TO_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>Tage bis 8 Std.</td>
                        <td v-text="Helpers.formatPrice(rate_settings.UP_TO_8_HOURS)"></td>
                        <td v-text="Helpers.numberOfDaysWithUpTo8Hours()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfDaysWithUpTo8Hours() * rate_settings.UP_TO_8_HOURS)"></td>
                    </tr>
                    <tr>
                        <td>An-/Abreisetage</td>
                        <td v-text="Helpers.formatPrice(rate_settings.FROM_8_TO_24_HOURS)"></td>
                        <td v-text="Helpers.numberOfArrivalAndDepartureDays()"></td>
                        <td v-text="Helpers.formatPrice(Helpers.numberOfArrivalAndDepartureDays() * rate_settings.FROM_8_TO_24_HOURS)"></td>
                    </tr>
                    <tr>
                        <td></td>
                        <td></td>
                        <td>Gesamtkosten Verpflegungsmehraufwand:</td>
                        <td class="tw-font-bold" v-text="Helpers.formatPrice(Helpers.dailyAllowanceTotalCosts())"></td>
                    </tr>
                    </tbody>
                </table>
                <div class="tw-text-blue-grey tw-italic tw-mt-2"
                    v-show="travel_expense_report.travel_information.daily_allowance">
                        Erstatteter Verpflegungsmehraufwand ist steuerlich nicht nochmals ansetzbar. Bei Auslandsreisen werden nur die deutschen
                        Pauschalen erstattet; in der Einkommensteuer ist lediglich die Differenz zu den Auslandspauschalen absetzbar.
                </div>
            </div>
        </div>

        <div v-show="step === 8" class="md:tw-w-2/3">
            <div class="tw-text-lg tw-font-bold">
                8/9 Kostenrechnung
            </div>

            <div class="tw-flex tw-mt-4">
                <div class="tw-w-1/2">
                    <bordered-input>
                        <template #label>
                            <label for="cost_center">
                                Kostenstelle
                            </label>
                        </template>
                        <template #input>
                            <select name="cost_center" id="cost_center" v-model="travel_expense_report.cost_center_id">
                                <option value="">
                                    Kostenstelle auswählen...
                                </option>

                                <option v-for="costCenter in costCenters" :value="costCenter.id">
                                    {{ costCenter.name }}
                                </option>
                            </select>
                        </template>
                    </bordered-input>
                </div>

                <div class="tw-w-1/2 tw-pl-4">

                    <bordered-input>
                        <template #label>
                            <label for="cost_carrier">
                                Kostenträger
                            </label>
                        </template>
                        <template #input>
                            <cost-carrier-select
                                    disable-branch-nodes
                                    :cost-centers="costCarriers"
                                    name="cost_carrier"
                                    id="cost_carrier"
                                    :value="travel_expense_report.cost_carrier_id"
                                    @change="onCostCarrierChanged"
                            ></cost-carrier-select>
                        </template>
                    </bordered-input>
                </div>
            </div>
        </div>

        <div v-show="step === 9">
            <div class="tw-text-lg tw-font-bold tw-mb-4">
                9/9 Bestätigen & Speichern
            </div>

            <button class="cu-link" @click="showConclusion">
                Zusammenfassung ansehen
            </button>

            <div class="tw-flex tw-items-center tw-py-1 tw-mt-4">
                <check-box
                        id="confirmation"
                        :initial-value="true"
                        name="confirmation"
                        v-model="travel_expense_report.confirmation"
                ></check-box>
                <label class="tw-ml-1" :for="'confirmation'">
                    Ich bestätige die Auslagenerstattung und verzichte auf eine Unterschrift.
                </label>
            </div>

            <div class="tw-mt-4">
                <button type="button" class="tw-w-24 cu-btn-default tw-mr-1" @click="previousStep" v-show="step > 1">
                    Zurück
                </button>

                <button class="tw-w-24 cu-btn tw-mr-1" @click="save(true)">
                    Speichern
                </button>

                <button class="tw-w-24 cu-btn-default" @click="save(false)">
                    Entwurf
                </button>
            </div>
        </div>

        <div class="tw-mt-4" v-show="step < 9">
            <button type="button" class="cu-btn-default tw-mr-1" @click="previousStep" v-show="step > 1">
                Zurück
            </button>

            <button type="button" class="cu-btn" @click="nextStep" v-show="step !== 0">
                Weiter
            </button>
        </div>
    </div>
</template>

<script>
    import BorderedInput from "../Redesign/BorderedInput";
    import MultiFileUpload from "../Redesign/MultiFileUpload";
    import CheckBox from "../Redesign/CheckBox";
    import Radio from "../Redesign/Radio";
    import CostCarrierSelect from "../CostCarrierSelect";
    import ConclusionModal from "./ConclusionModal";
    import axios from 'axios';

    import {Constants, TravelTypes, OvernightPaymentTypes, Helpers, ExpenseTypes} from "./TravelExpenseReport";
import { data } from "jquery";

    export default {
        components: {
            BorderedInput,
            MultiFileUpload,
            CheckBox,
            Radio,
            CostCarrierSelect,
            ConclusionModal,
        },

        props: {
            companyCars: {
                type: Array,
                required: true,
            },
            costCenters: {
                type: Array,
                required: true,
            },
            costCarriers: {
                type: Array,
                required: true,
            },
            initialTravelExpenseReport: {
                type: Object,
                required: false,
                default: null,
            },
            initialExpenseReimbursement: {
                type: Object,
                required: false,
                default: null,
            },

            redirectUrlAfterSave: {
                type: String,
                required: true,
            },
            oldData: {
                type: Object,
                required: true
            },
            expenseReimbursementAttachmentTypeId: {
                type: Number,
                required: true
            },
            companyCarAttachmentTypeId: {
                type: Number,
                required: true
            },
            publicTransportationAttachmentTypeId: {
                type: Number,
                required: true
            },
            overnightAttachmentTypeId: {
                type: Number,
                required: true
            },
            otherCostAttachmentTypeId: {
                type: Number,
                required: true
            },
            initialStep: {
                type: Number,
                required: true
            },
            initialType: {
                type: Number,
                required: false,
            },
            initialRates: {
                type: Object,
                required: true,
            }
        },

        mounted() {
            const settings = this.initialRates;

            this.dailyAllowanceUpTo8Hours = settings.UP_TO_8_HOURS ?? Constants.DAILY_ALLOWANCE_UP_TO_8_HOURS;
            this.dailyAllowance8To24Hours = settings.FROM_8_TO_24_HOURS ?? Constants.DAILY_ALLOWANCE_8_TO_24_HOURS;
            this.dailyAllowance24Hours = settings.ABOVE_24_HOURS ?? Constants.DAILY_ALLOWANCE_24_HOURS;
        },

        created() {
            console.log(this.oldData);
        },

        data() {
            let data = {
                Constants: Constants,
                TravelTypes: TravelTypes,
                OvernightPaymentTypes: OvernightPaymentTypes,
                ExpenseTypes: ExpenseTypes,

                step: this.initialStep,
                errors: [],
                isSaving: false,
                rate_settings: this.initialRates ?? [],

                expense_reimbursement: this.initialExpenseReimbursement !== null ? this.initialExpenseReimbursement : {
                    id: null,
                    user_id: null,
                    active: false,
                    complete: false,
                    costs: [
                        {
                            date: null,
                            type: null,
                            amount: null,
                            cost_carrier_center: null,
                            cost_carrier: null
                        }
                    ],
                    attachment_ids: [],
                    expense_confirmation: false
                },

                travel_expense_report: this.initialTravelExpenseReport !== null ? this.initialTravelExpenseReport : {
                    id: null,
                    user_id: null,
                    passengers: null,
                    reason_for_trip: null,
                    route: null,
                    complete: false,
                    cost_center_id: null,
                    cost_carrier_id: null,
                    confirmation: false,
                    create_for_ceo: false,

                    expense_type: this.initialType != null ? this.initialType : 0,

                    travel_information: {
                        id: null,
                        period: [
                            {
                                date: null,
                                from: null,
                                to: null,
                                entire_day: false,
                            }
                        ],
                        had_overnight: false,
                        transportation_type: null,
                        company_car: {
                            self_paid: false,
                            company_car_id: null,
                            costs: [
                                {
                                    costs: null,
                                    cost_description: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        private_car: {
                            licence_plate: null,
                            costs: [
                                {
                                    mileage_start: null,
                                    mileage_end: null,
                                    number_of_passengers: 0,
                                }
                            ]
                        },
                        public_transportation: {
                            self_paid: null,
                            costs: [
                                {
                                    type: null,
                                    amount: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        overnight_stay: {
                            self_paid: null,
                            hotel: null,
                            payment_method: null,
                            costs: [
                                {
                                    person_name: null,
                                    number_of_nights: null,
                                    amount_per_night: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        other_cost: {
                            self_paid: null,
                            costs: [
                                {
                                    type: null,
                                    amount: null,
                                }
                            ],
                            attachment_ids: [],
                        },
                        daily_allowance: null,
                    }
                },
            }

            data.Helpers = new Helpers(data.travel_expense_report, data.expense_reimbursement, data.rate_settings);

            return data;
        },
        watch: {
            expense_reimbursement: {
                immediate: true,
                deep: false,
                handler(val) {
                    console.log('expense_reimbursement changed:', val);
                }
            },
            'travel_expense_report.travel_information.transportation_type'(newVal) {
                if (newVal === Constants.TRANSPORTATION_TYPE_COMPANY_CAR) {
                    this.resetPublicTransportation();
                }
                else if (newVal === Constants.TRANSPORTATION_TYPE_PUBLIC_TRANSPORTATION) {
                    this.resetCompanyCar();
                    this.expense_reimbursement.travel_information.company_car.self_paid = false;
                }
            },
            'travel_expense_report.travel_information.company_car.self_paid'(newVal) {
                if (!newVal) {
                    this.travel_expense_report.travel_information.company_car.costs = [
                        { costs: null, cost_description: null }
                    ];
                    this.travel_expense_report.travel_information.company_car.attachment_ids = [];
                }
            },
            'travel_expense_report.travel_information.public_transportation.self_paid'(newVal) {
                if (!newVal) {
                    this.travel_expense_report.travel_information.public_transportation.costs = [
                        { type: null, amount: null }
                    ];
                    this.travel_expense_report.travel_information.public_transportation.attachment_ids = [];
                }
            },
            'travel_expense_report.travel_information.overnight_stay.self_paid'(newVal) {
                if (newVal) {
                    this.travel_expense_report.travel_information.overnight_stay.payment_method = "Auslage";
                } else {
                    this.travel_expense_report.travel_information.overnight_stay.hotel = null;
                    this.travel_expense_report.travel_information.overnight_stay.payment_method = null;
                    this.travel_expense_report.travel_information.overnight_stay.costs = [
                        {
                            person_name: null,
                            number_of_nights: null,
                            amount_per_night: null,
                        }
                    ];
                    this.travel_expense_report.travel_information.overnight_stay.attachment_ids = [];
                }
            },
            'travel_expense_report.travel_information.other_cost.self_paid'(newVal) {
                if (!newVal) {
                    this.travel_expense_report.travel_information.other_cost.costs = [
                        { type: null, amount: null }
                    ];
                    this.travel_expense_report.travel_information.other_cost.attachment_ids = [];
                }
            },
        },
        methods: {
            resetCompanyCar() {
                this.travel_expense_report.travel_information.company_car = {
                    self_paid: null,
                    company_car_id: null,
                    costs: [
                        {
                            costs: null,
                            cost_description: null,
                        }
                    ],
                    attachment_ids: [],
                };
            },

            resetPublicTransportation() {
                this.travel_expense_report.travel_information.public_transportation = {
                    self_paid: null,
                    costs: [
                        {
                            type: null,
                            amount: null,
                        }
                    ],
                    attachment_ids: [],
                };
            },

            updateAttachmentIds(attachmentIds, attachmentTypeId) {
                if (attachmentTypeId === this.companyCarAttachmentTypeId) {
                    this.travel_expense_report.travel_information.company_car.attachment_ids = attachmentIds ?? [];
                } else if (attachmentTypeId === this.publicTransportationAttachmentTypeId) {
                    this.travel_expense_report.travel_information.public_transportation.attachment_ids = attachmentIds ?? [];
                } else if (attachmentTypeId === this.overnightAttachmentTypeId) {
                    this.travel_expense_report.travel_information.overnight_stay.attachment_ids = attachmentIds ?? [];
                } else if (attachmentTypeId === this.otherCostAttachmentTypeId) {
                    this.travel_expense_report.travel_information.other_cost.attachment_ids = attachmentIds ?? [];
                } else if (attachmentTypeId === this.expenseReimbursementAttachmentTypeId) {
                    this.expense_reimbursement.attachment_ids = attachmentIds ?? [];
                }
            },

            nextStep() {
                const onSuccess = response => {
                    if (this.travel_expense_report.expense_type == 1) {
                        this.step = 0;
                    } else {
                        if (this.step == 4 && !this.travel_expense_report.travel_information.had_overnight) {
                            this.travel_expense_report.travel_information.overnight_stay.self_paid = false;
                            this.step = 6;
                            return;
                        }

                        if (this.step <= 0) {
                            this.step = 1;
                        } else {
                            this.step++;
                        }
                    }
                };
                const onError = () => {};
                this.validate(onSuccess, onError);
            },

            previousStep() {
                if (this.step == 1) {
                    this.step = -1;
                } else if (this.step == 6 && !this.travel_expense_report.travel_information.had_overnight) {
                    this.step = 4;
                 } else if (this.step > -1) {
                    this.step--;
                }
            },

            validate(onSuccess, onError) {
                this.errors = [];

                if (this.step == 0) {
                    axios
                        .post('/travel-expense-report/wizard/validate/' + this.step, this.expense_reimbursement)
                        .then(response => {
                            if (typeof onSuccess === 'function') {
                                onSuccess(response);
                            }
                        })
                        .catch(error => {
                            if (!!error.response.data.errors) {
                                this.errors = error.response.data.errors;
                                onError();
                            }
                        })
                } else if (this.step >= 1) {
                    axios
                        .post('/travel-expense-report/wizard/validate/' + this.step, this.travel_expense_report)
                        .then(response => {
                            if (typeof onSuccess === 'function') {
                                onSuccess(response);
                            }
                        })
                        .catch(error => {
                            if (!!error.response.data.errors) {
                                this.errors = error.response.data.errors;
                                onError();
                            }
                        })
                } else if (this.step == -1) {
                    onSuccess(true);
                }
            },

            showConclusion() {
                this.$modal.show(ConclusionModal, {
                    travelExpenseReport: this.travel_expense_report,
                    costCenters: this.costCenters,
                    costCarriers: this.costCarriers,
                }, {height: 'auto', width: '66%'});
            },

            save(saveAsDraft) {
                if (this.isSaving === true) {
                    return;
                }

                this.isSaving = true;

                if (this.step === 0) {
                    this.expense_reimbursement.active = true;
                }

                let onSuccess = validationResponse => {
                    this.travel_expense_report.complete = saveAsDraft;
                    this.expense_reimbursement.complete = saveAsDraft;

                    if (this.travel_expense_report.id > 0) {
                        axios
                            .patch('/travel-expense-report/wizard/' + this.travel_expense_report.id, this.travel_expense_report)
                            .then(response => {
                                window.location.href = this.redirectUrlAfterSave;
                            })
                            .catch(error => {
                                this.isSaving = false;
                            });
                    } else if (this.expense_reimbursement.id > 0) {
                        axios
                            .patch('/expense-reimbursement/wizard/' + this.expense_reimbursement.id, this.expense_reimbursement)
                            .then(response => {
                                window.location.href = this.redirectUrlAfterSave;
                            })
                            .catch(error => {
                                this.isSaving = false;
                            });
                    } else {
                        if (this.step === 0) {
                            axios
                                .post('/travel-expense-report/wizard/', this.expense_reimbursement)
                                .then(response => {
                                    window.location.href = this.redirectUrlAfterSave;
                                })
                                .catch(error => {
                                    this.isSaving = false;
                                });

                        } else if (this.step >= 1) {
                            axios
                                .post('/travel-expense-report/wizard/', this.travel_expense_report)
                                .then(response => {
                                    window.location.href = this.redirectUrlAfterSave;
                                })
                                .catch(error => {
                                    this.isSaving = false;
                                });
                        }
                    }
                };

                let onError = () => {
                  this.isSaving = false;
                };

                try {
                  this.validate(onSuccess, onError);
                } catch (e) {
                  onError();
                }
            },

            deleteDay(day) {
                const period = this.travel_expense_report.travel_information.period;
                const index = period.indexOf(day);

                if (this.travel_expense_report.travel_information.had_overnight && index === period.length - 1 && index > 0) {
                    period[index - 1].to = null;
                    period[index - 1].entire_day = false;
                }

                period.splice(index, 1);

                this.refreshDays();
            },

            formatDate(daysToAdd) {
                const date = new Date(this.travel_expense_report.travel_information.period[0].date);
                date.setDate(date.getDate() + daysToAdd);

                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');

                return `${year}-${month}-${day}`;
            },

            refreshDays() {
                const period = this.travel_expense_report.travel_information.period;

                if (period.length >= 3) {
                    period.slice(1, period.length - 1).forEach((day) => {
                        if (this.travel_expense_report.travel_information.had_overnight) {
                            day.to = "00:00";
                        }
                        day.entire_day = this.travel_expense_report.travel_information.had_overnight;
                    });
                }

                period.forEach((day, i) => {
                    if (i !== 0) {
                        day.date = this.formatDate(i);
                        if (this.travel_expense_report.travel_information.had_overnight) {
                            day.from = "00:00";
                        }
                    } else if (i === 0 && period.length > 1) {
                        if (this.travel_expense_report.travel_information.had_overnight) {
                            day.to = "00:00";
                        }
                    }
                });
            },

            minTime(day) {
                if (day.to != null && day.to < day.from) {
                    day.to = day.from;
                }
            },

            addDay() {
                const period = this.travel_expense_report.travel_information.period;
                const formattedDate = this.formatDate(period.length);

                period.push({
                    date: formattedDate,
                    from: null,
                    to: null,
                    entire_day: false,
                });

                if (period.length >= 3) {
                    period.forEach((day, i) => {
                        day.entire_day = i > 0 && i < period.length - 1 && this.travel_expense_report.travel_information.had_overnight;
                    });
                }

                this.refreshDays();
            },

            addCompanyCarCosts() {
                this.travel_expense_report.travel_information.company_car.costs.push({
                    costs: null,
                    cost_description: null,
                });
            },

            deleteCompanyCarCost(cost) {
                this.travel_expense_report.travel_information.company_car.costs = this.travel_expense_report.travel_information.company_car.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addPrivateCarCosts() {
                this.travel_expense_report.travel_information.private_car.costs.push({
                    mileage_start: null,
                    mileage_end: null,
                    number_of_passengers: null,
                });
            },

            deletePrivateCarCost(cost) {
                this.travel_expense_report.travel_information.private_car.costs = this.travel_expense_report.travel_information.private_car.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addPublicTransportationCost() {
                this.travel_expense_report.travel_information.public_transportation.costs.push({
                    type: null,
                    amount: null,
                });
            },

            deletePublicTransportationCost(cost) {
                this.travel_expense_report.travel_information.public_transportation.costs = this.travel_expense_report.travel_information.public_transportation.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addOvernightStayCosts() {
                this.travel_expense_report.travel_information.overnight_stay.costs.push({
                    person_name: null,
                    number_of_nights: null,
                    amount_per_night: null,
                });
            },

            deleteOvernightStayCost(cost) {
                this.travel_expense_report.travel_information.overnight_stay.costs = this.travel_expense_report.travel_information.overnight_stay.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            addOtherCosts() {
                this.travel_expense_report.travel_information.other_cost.costs.push({
                    type: null,
                    amount: null,
                });
            },

            addExpense() {
                this.expense_reimbursement.costs.push({
                    date: null,
                    type: null,
                    amount: null,
                    cost_carrier_center: null,
                    cost_carrier: null
                });
            },

            deleteOtherCost(cost) {
                this.travel_expense_report.travel_information.other_cost.costs = this.travel_expense_report.travel_information.other_cost.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            deleteExpense(cost) {
                this.expense_reimbursement.costs = this.expense_reimbursement.costs.filter(currentCost => {
                    return cost !== currentCost;
                })
            },

            onCostCarrierChanged(id) {
                this.travel_expense_report.cost_carrier_id = id;
            },

            onExpenseCarrierChanged(index, value) {
                this.expense_reimbursement.costs[index].cost_carrier = value;
            },

            isEditWizard() {
                return this.initialExpenseReimbursement !== null || this.initialTravelExpenseReport !== null;
            }
        },
    }
</script>
